import { useMemo, useEffect } from "react";

import { useFormikContext } from "formik";

import { useFlags } from "flagsmith/react";
import { USER_FLAGS_ENUM } from "@utils/featureFlags";

import type { FormikInitialValuesNewRequest } from "../../model/form.model";
import type { PaymentMethod } from "../../model/product.cart.model";

import { useNewRequestContext } from "../../state/NewRequestProvider";

import { addProductToCart } from "../../application/usecases/products/addProductToCart";
import { repriceCartItem } from "../../application/usecases/products/repriceCartItem";
import { GetQuantityOptionsUseCase } from "../../application/usecases/products/quantityOptions";
import { recalcCartTotalsByFreight } from "../../application/usecases/products/recalcCartTotalsByFreight";
import { applyTaxAccordingFreight } from "../../domain/calculations/applyTaxAccordingFreight";

export type CatalogOption = {
  key: string;
  label: string;
  code?: string;
};

export function useProductsStep() {
  const flag = useFlags([USER_FLAGS_ENUM.USER_NEW_REQUEST_LIST_AMOUNT]);

  const { services, state, dispatch } = useNewRequestContext();

  const formik = useFormikContext<FormikInitialValuesNewRequest>();

  const cart = state.products.cart;
  const pricingStatus = state.products.pricingStatus;
  const error = state.products.error;

  const freightType = formik.values.purchase.freightType;
  const purchase = formik.values.purchase;

  const cartView = useMemo(() => {
    return cart.map((it) => {
      const displayUnitPrice = applyTaxAccordingFreight(
        freightType,
        it.unitPrice,
        it.freightUnitPrice
      );

      return {
        ...it,
        displayUnitPrice,
      };
    });
  }, [cart, freightType]);

  const totals = useMemo(() => {
    const subtotal = cart.reduce(
      (acc, it) => acc + (it.quantity > 0 ? it.subtotal : 0),
      0
    );
    const total = cart.reduce(
      (acc, it) => acc + (it.quantity > 0 ? it.total : 0),
      0
    );
    return { subtotal, total };
  }, [cart]);

  const catalogOptions: CatalogOption[] = useMemo(() => {
    return (
      state.purchase.customer?.products.map((p) => ({
        key: p.id,
        label: p.description,
      })) ?? []
    );
  }, [state.purchase.customer?.products]);

  const listAmount = flag.user_new_request_create_option_list_amount;
  const quantityOptions = useMemo(() => {
    return new GetQuantityOptionsUseCase(listAmount).execute();
  }, [listAmount]);

  async function addProduct(input: {
    productId: string;
    code: string;
    description: string;
    quantity: number;
  }) {
    dispatch({ type: "products/setError", value: undefined });
    dispatch({ type: "products/setPricingStatus", value: "loading" });

    try {
      const customer = state.purchase.customer;
      if (!customer?.id) throw new Error("Selecione um cliente.");

      const address = customer.addresses?.find(
        (a) => a.id === purchase.deliveryAddressId
      );
      if (!address) throw new Error("Selecione um endereço de entrega.");

      const filialId = state.purchase.customer?.withdrawBasis.find(
        (base) =>
          String(base.shippingPoint) === String(purchase.withdrawBasisId)
      )?.filialID;

      if (!filialId) throw new Error("Selecione uma base de retirada válida.");

      const next = await addProductToCart({
        services,
        freightType: purchase.freightType,
        customer: { id: customer.id },
        filialId,
        address: {
          id: address.id,
          transportationZone: String(address.transportationZone ?? ""),
        },
        currentCart: cart,
        input,
        products: customer.products,
        lastPayment: state.lastPayment,
      });

      dispatch({ type: "products/setCart", value: next });
      dispatch({ type: "products/setPricingStatus", value: "success" });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Erro ao adicionar produto.";
      dispatch({ type: "products/setError", value: msg });
      dispatch({ type: "products/setPricingStatus", value: "error" });
    }
  }

  async function changeQty(itemId: string, qtyRaw: string) {
    const next = cart.map((c) =>
      c.id === itemId ? { ...c, quantity: toInt(qtyRaw) } : c
    );
    dispatch({ type: "products/setCart", value: next });
  }

  function remove(itemId: string) {
    dispatch({ type: "products/setError", value: undefined });

    const next = cart.filter((c) => c.id !== itemId);
    dispatch({ type: "products/setCart", value: next });
  }

  const canInteract = useMemo(() => {
    return Boolean(
      purchase.customerCnpj &&
        purchase.withdrawBasisId &&
        purchase.deliveryAddressId
    );
  }, [
    purchase.customerCnpj,
    purchase.withdrawBasisId,
    purchase.deliveryAddressId,
  ]);

  async function changePayment(itemId: string, payment: PaymentMethod) {
    dispatch({ type: "products/setError", value: undefined });
    dispatch({ type: "products/setPricingStatus", value: "loading" });

    try {
      const next = await repriceCartItem({
        services,
        freightType: purchase.freightType,
        currentCart: cart,
        targetId: itemId,
        patch: { payment },
      });

      dispatch({ type: "products/setCart", value: next });
      dispatch({ type: "products/setPricingStatus", value: "success" });
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : "Erro ao atualizar pagamento.";
      dispatch({ type: "products/setError", value: msg });
      dispatch({ type: "products/setPricingStatus", value: "error" });
    }
  }

  async function changeDeadline(itemId: string, deadlinePaymentRaw: string) {
    dispatch({ type: "products/setError", value: undefined });
    dispatch({ type: "products/setPricingStatus", value: "loading" });

    try {
      const newDeadline = toInt(deadlinePaymentRaw);

      const newPayment = newDeadline > 0 ? "B" : "T";
      const next = await repriceCartItem({
        services,
        freightType: purchase.freightType,
        currentCart: cart,
        targetId: itemId,
        patch: {
          deadlinePayment: toInt(deadlinePaymentRaw),
          payment: newPayment,
        },
      });

      dispatch({ type: "products/setCart", value: next });
      dispatch({ type: "products/setPricingStatus", value: "success" });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Erro ao atualizar prazo.";
      dispatch({ type: "products/setError", value: msg });
      dispatch({ type: "products/setPricingStatus", value: "error" });
    }
  }

  useEffect(() => {
    if (!cart.length) return;
    if (!canInteract) return;

    const next = recalcCartTotalsByFreight({ cart, freightType });

    const same =
      next.length === cart.length &&
      next.every(
        (n, i) => n.subtotal === cart[i].subtotal && n.total === cart[i].total
      );

    if (same) return;

    dispatch({ type: "products/setCart", value: next });
  }, [cart, freightType, dispatch, canInteract]);

  return {
    state: {
      cart,
      cartView,
      totals,
      pricingStatus,
      error,
      canInteract: canInteract,
      catalogOptions,
      quantityOptions,
      freightType,
    },
    action: {
      changePayment,
      changeDeadline,
      addProduct,
      changeQty,
      remove,
    },
  };
}

function toInt(v: string): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : 0;
}

// ex: 1000 ou "1000" => "1.000"
export function formatQty(qty?: number | string): string {
  if (qty === undefined || qty === null) return "-";

  const value = typeof qty === "string" ? parseFloat(qty) : qty;

  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
}

// ex: "1.000" => "1000"
export function parseDotQuantity(str: string): string {
  return String(Number(str.replace(/\./g, "")));
}
